import React, { useState } from 'react';
import { 
  CheckCircle, HelpCircle, FileCheck, Award, CreditCard, 
  Sparkles, ArrowRight, ShieldCheck, UserCheck, AlertCircle 
} from 'lucide-react';
import { INSTITUTION_INFO } from '../data/portalData';

export const AdmissionsRequirements = ({ setActiveView }) => {
  // Interactive Eligibility Evaluator State
  const [userQualification, setUserQualification] = useState('first-degree');
  const [userExperience, setUserExperience] = useState('3-5');
  const [userDiscipline, setUserDiscipline] = useState('business');

  // Compute recommended eligibility
  const getEligibilityFeedback = () => {
    if (userQualification === 'masters') {
      return {
        tier: "Doctoral Programmes Eligible",
        eligibleFor: [
          "Doctor of Philosophy (PhD) in Business Administration",
          "Doctor of Business Administration (DBA)",
          "Doctor of Public Administration (DPA)",
          "Doctor of Accounting & Finance"
        ],
        status: "High Eligibility",
        color: "text-[#0B2644] bg-amber-50/80 border-[#7A470E]/60",
        message: "Your Master's degree qualifies you for both our research doctorate (PhD) and executive practitioner doctorate (DBA/DPA/DAF)."
      };
    } else if (userQualification === 'first-degree') {
      if (userExperience === '0-2') {
        return {
          tier: "Postgraduate Diploma or Early-Career Master's",
          eligibleFor: [
            "Postgraduate Diploma in Business Administration (PGD)",
            "Executive Master of Business Administration (Conditional on Interview)",
            "Professional Diplomas & Certificates"
          ],
          status: "Eligible for PGD / Conditional MBA",
          color: "text-[#0B2644] bg-slate-50 border-slate-200",
          message: "Master's programmes normally require at least 3 years of post-graduation professional experience. You qualify directly for PGD or may request an executive admissions interview."
        };
      } else {
        return {
          tier: "Executive Master's Programmes Fully Eligible",
          eligibleFor: [
            "Master of Business Administration (MBA)",
            "Executive Master of Public Administration (EMPA)",
            "Executive Master of Accounting and Finance (EMAF)",
            "Postgraduate Diploma in Business Administration (PGD)"
          ],
          status: "Fully Eligible for Master's",
          color: "text-[#0B2644] bg-amber-50/80 border-[#7A470E]/60",
          message: "Congratulations! Your recognised first degree combined with 3+ years of professional experience meets the full entry criteria for our executive Master's programmes."
        };
      }
    } else if (userQualification === 'hnd') {
      return {
        tier: "Postgraduate Diploma (PGD) Route",
        eligibleFor: [
          "Postgraduate Diploma in Business Administration (PGD)",
          "Professional Diploma in Public Administration",
          "Professional Diploma in Accounting & Finance"
        ],
        status: "Eligible for PGD",
        color: "text-[#0B2644] bg-slate-50 border-slate-200",
        message: "Your Higher National Diploma (HND) qualifies you for our 1-year PGD, which serves as a recognised bridge to full MBA or M.Sc admission."
      };
    } else if (userQualification === 'pgd') {
      return {
        tier: "Executive Master's Programmes Eligible",
        eligibleFor: [
          "Master of Business Administration (MBA)",
          "Executive Master of Public Administration (EMPA)",
          "Executive Master of Accounting and Finance (EMAF)"
        ],
        status: "Fully Eligible for Master's",
        color: "text-[#0B2644] bg-amber-50/80 border-[#7A470E]/60",
        message: "Your Postgraduate Diploma from a recognised university qualifies you directly for our executive Master's programmes."
      };
    } else {
      return {
        tier: "Professional Certificate & Diploma Programmes",
        eligibleFor: [
          "Diploma in Business Administration",
          "Professional Certificate in Public Administration",
          "Professional Certificate in Accounting & Finance"
        ],
        status: "Certificate / Diploma Eligible",
        color: "text-slate-700 bg-slate-50 border-slate-200",
        message: "With 5 SSCE credit passes (including English & Maths), you qualify for our professional certificates and executive diplomas."
      };
    }
  };

  const eligibility = getEligibilityFeedback();

  return (
    <section id="admissions" className="py-20 bg-white border-b-2 border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-amber-50 px-3 py-1 rounded-lg border border-[#7A470E]/40 mb-3 font-sc">
            <UserCheck className="w-3.5 h-3.5 text-[#7A470E]" />
            <span>Clear & Transparent Criteria</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-[#0B2644] font-sc">
            Admissions Standards & Criteria
          </h2>
          <p className="mt-4 text-base text-slate-600">
            Admissions to the AKSU Business School follow the National Universities Commission (NUC) benchmark 
            academic standards, designed to admit leaders with proven academic ability and executive drive.
          </p>
        </div>

        {/* 2-Column Layout: Requirements Grid vs Interactive Self-Check Tool */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column (7 cols): Formal Academic Requirements by Category */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Doctoral Requirements */}
            <div className="translucent-card-light rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
                <div>
                  <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                    Tier 1 · Doctorate
                  </span>
                  <h3 className="text-xl font-black font-sc text-[#0B2644]">
                    PhD & DBA Programmes
                  </h3>
                </div>
                <span className="px-3 py-1 text-xs font-black rounded-lg bg-[#0B2644] text-white font-sc">
                  Doctoral Degree
                </span>
              </div>
              <ul className="space-y-2.5 text-xs sm:text-sm text-slate-700">
                <li className="flex items-start space-x-2.5">
                  <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                  <span><strong>Master's Degree:</strong> A recognised Master's degree (M.Sc, MBA, M.Phil) with minimum 60% average (or CGPA of 3.50/5.00) in relevant disciplines.</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                  <span><strong>Executive Track Record:</strong> Minimum 5 years in managerial or executive positions for the Doctor of Business Administration (DBA).</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                  <span><strong>Doctoral Concept Note:</strong> A 3 to 5-page preliminary research proposal outlining research problem, objectives, and industry relevance.</span>
                </li>
              </ul>
            </div>

            {/* Master's Requirements */}
            <div className="translucent-card-light rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
                <div>
                  <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                    Tier 2 · Master's
                  </span>
                  <h3 className="text-xl font-black font-sc text-[#0B2644]">
                    MBA, EMPA & EMAF Programmes
                  </h3>
                </div>
                <span className="px-3 py-1 text-xs font-black rounded-lg bg-amber-100 text-[#5C3306] font-sc">
                  Executive Master's
                </span>
              </div>
              <ul className="space-y-2.5 text-xs sm:text-sm text-slate-700">
                <li className="flex items-start space-x-2.5">
                  <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                  <span><strong>Bachelor's Degree:</strong> Minimum Second Class Honours (Lower Division) from a recognised university, OR a PGD with credit pass.</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                  <span><strong>Work Experience:</strong> Minimum 3 years post-NYSC corporate, public sector, or entrepreneurial experience.</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                  <span><strong>SSCE Baseline:</strong> Five credit passes including English Language and Mathematics at WASSCE/NECO/GCE O'Level.</span>
                </li>
              </ul>
            </div>

            {/* PGD & Diploma Requirements */}
            <div className="translucent-card-light rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
                <div>
                  <span className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc">
                    Tier 3 · Postgrad Diploma & Certificate
                  </span>
                  <h3 className="text-xl font-black font-sc text-[#0B2644]">
                    PGD & Professional Diplomas
                  </h3>
                </div>
                <span className="px-3 py-1 text-xs font-black rounded-lg bg-slate-200 text-slate-800 font-sc">
                  Diploma / Cert
                </span>
              </div>
              <ul className="space-y-2.5 text-xs sm:text-sm text-slate-700">
                <li className="flex items-start space-x-2.5">
                  <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                  <span><strong>PGD:</strong> Third Class Honours degree or Higher National Diploma (HND) with minimum Lower Credit in relevant field.</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                  <span><strong>Professional Diploma/Certificate:</strong> Recognised National Diploma, NCE, or SSCE credit passes with industry employment.</span>
                </li>
              </ul>
            </div>

          </div>

          {/* Right Column (5 cols): Interactive Candidate Eligibility Evaluator */}
          <div className="lg:col-span-5">
            <div className="translucent-card-navy text-white rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#7A470E]/15 rounded-full blur-3xl pointer-events-none" />

              <div className="flex items-center space-x-2 pb-2">
                <Sparkles className="w-5 h-5 text-[#B87A28]" />
                <h3 className="text-xl font-black font-sc text-white">
                  Interactive Eligibility Checker
                </h3>
              </div>
              <p className="text-xs text-slate-300">
                Select your academic credentials to instantly evaluate your eligibility across all AKSU Business School tracks.
              </p>

              {/* Form Controls */}
              <div className="mt-6 space-y-4 text-xs">
                
                <div>
                  <label className="block text-slate-300 font-semibold mb-1.5 font-sc">
                    1. Highest Academic Qualification Held:
                  </label>
                  <select
                    value={userQualification}
                    onChange={(e) => setUserQualification(e.target.value)}
                    className="w-full bg-[#030F1C] border border-[#7A470E]/40 rounded-xl px-3.5 py-2.5 text-white focus:outline-none focus:border-[#7A470E] font-medium"
                  >
                    <option value="masters">Master's Degree (M.Sc, MBA, M.Phil)</option>
                    <option value="pgd">Postgraduate Diploma (PGD)</option>
                    <option value="first-degree">Bachelor's Degree (B.Sc, B.A - Min 2nd Class)</option>
                    <option value="hnd">Higher National Diploma (HND)</option>
                    <option value="nd-ssce">National Diploma / O'Level (5 Credits)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 font-semibold mb-1.5 font-sc">
                    2. Post-Graduation Work Experience:
                  </label>
                  <select
                    value={userExperience}
                    onChange={(e) => setUserExperience(e.target.value)}
                    className="w-full bg-[#030F1C] border border-[#7A470E]/40 rounded-xl px-3.5 py-2.5 text-white focus:outline-none focus:border-[#7A470E] font-medium"
                  >
                    <option value="0-2">Less than 3 years (Early Career)</option>
                    <option value="3-5">3 - 5 years (Mid-Level Manager / Specialist)</option>
                    <option value="6-10">6 - 10 years (Senior Executive / Department Head)</option>
                    <option value="10+">Over 10 years (C-Suite / Director / Board Level)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 font-semibold mb-1.5 font-sc">
                    3. Professional Discipline:
                  </label>
                  <select
                    value={userDiscipline}
                    onChange={(e) => setUserDiscipline(e.target.value)}
                    className="w-full bg-[#030F1C] border border-[#7A470E]/40 rounded-xl px-3.5 py-2.5 text-white focus:outline-none focus:border-[#7A470E] font-medium"
                  >
                    <option value="business">Management, Economics, Administration</option>
                    <option value="finance">Accounting, Banking, Auditing, Taxation</option>
                    <option value="public">Public Service, Civil Service, Policy & MDAs</option>
                    <option value="stem">Engineering, Sciences, Health, Legal, Others</option>
                  </select>
                </div>

              </div>

              {/* Dynamic Evaluation Output Card */}
              <div className="mt-6 pt-5 border-t border-slate-800">
                <div className={`p-4 rounded-2xl border ${eligibility.color} bg-white text-slate-900 shadow-md`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-extrabold uppercase text-xs tracking-wider font-sc text-[#0B2644]">
                      {eligibility.tier}
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#0B2644] text-white font-sc">
                      {eligibility.status}
                    </span>
                  </div>

                  <p className="text-xs text-slate-700 leading-relaxed mb-3">
                    {eligibility.message}
                  </p>

                  <div className="space-y-1 text-xs">
                    <div className="font-bold text-slate-800 font-sc">Eligible Programme Pathways:</div>
                    <ul className="space-y-1 pl-4 list-disc text-slate-600">
                      {eligibility.eligibleFor.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Advisory Note */}
                  <div className="mt-3 pt-2.5 border-t border-slate-200/60 flex items-start space-x-2 text-[11px] text-slate-500 leading-normal">
                    <AlertCircle className="w-3.5 h-3.5 text-[#7A470E] shrink-0 mt-0.5" />
                    <span><strong>Advisory Note:</strong> This self-evaluation is indicative. Meeting minimum entry criteria qualifies you to submit an application dossier for board screening, but does not guarantee admission.</span>
                  </div>
                </div>

                <div className="mt-5">
                  <button
                    onClick={() => setActiveView('apply')}
                    className="w-full py-3 bg-[#7A470E] hover:bg-[#5C3306] text-white font-black text-xs sm:text-sm rounded-xl transition-all shadow-lg flex items-center justify-center space-x-2 cursor-pointer font-sc"
                  >
                    <span>Proceed to Apply Based on Your Credentials</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* Statutory Admissions Disclaimer Band */}
        <div className="mt-12 p-6 rounded-3xl bg-amber-50/70 border-2 border-amber-300 text-xs text-slate-800 space-y-2 shadow-2xs">
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-5 h-5 text-[#7A470E]" />
            <h4 className="font-extrabold font-sc uppercase tracking-wider text-[#0B2644] text-xs">
              Statutory University Admissions Disclaimer
            </h4>
          </div>
          <p className="leading-relaxed text-slate-700 font-medium">
            Satisfying the minimum academic or professional entry requirements qualifies an applicant for consideration and admissions screening, but does <strong>NOT</strong> constitute an offer or guarantee of admission. Offers of provisional admission are competitive, merit-based, and subject to authentic credential verification, official academic transcript audit, and final ratification by the Postgraduate Admissions Board and University Senate.
          </p>
        </div>

      </div>
    </section>
  );
};
